<template>
  <div class="engagement-topic-selector">
    <label class="block text-sm font-medium text-gray-700 mb-2">
      Topics
    </label>

    <!-- Selected Topics as Tags -->
    <div v-if="selectedTopics.length > 0" class="flex flex-wrap gap-2 mb-3">
      <span
        v-for="topic in selectedTopics"
        :key="topic.id"
        class="inline-flex items-center gap-1.5 px-3 py-1 bg-primary-100 text-primary-800 text-sm rounded-full hover:bg-primary-200 transition-colors"
      >
        {{ topic.name }}
        <button
          @click="toggleTopic(topic.id)"
          class="hover:text-primary-900 focus:outline-none"
          type="button"
        >
          <font-awesome-icon :icon="['fas', 'times']" class="h-3 w-3" />
        </button>
      </span>
    </div>

    <!-- Dropdown Button -->
    <div class="relative">
      <button
        @click="isOpen = !isOpen"
        type="button"
        class="w-full px-4 py-2 text-left border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-dark focus:border-transparent transition-all bg-white hover:bg-gray-50"
      >
        <span class="text-gray-700">
          {{ selectedTopics.length > 0 ? `${selectedTopics.length} topic(s) selected` : 'Select topics...' }}
        </span>
        <font-awesome-icon
          :icon="['fas', isOpen ? 'chevron-up' : 'chevron-down']"
          class="float-right mt-1 text-gray-400"
        />
      </button>

      <!-- Dropdown Panel -->
      <div
        v-if="isOpen"
        class="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden"
      >
        <!-- Search Input -->
        <div class="p-3 border-b border-gray-200">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search topics..."
            class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-dark focus:border-transparent"
            @click.stop
          />
        </div>

        <!-- Options List -->
        <div class="overflow-y-auto max-h-64">
          <label
            v-for="topic in filteredTopics"
            :key="topic.id"
            class="flex items-center px-4 py-2.5 hover:bg-gray-50 cursor-pointer transition-colors"
          >
            <input
              type="checkbox"
              :checked="isSelected(topic.id)"
              @change="toggleTopic(topic.id)"
              class="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-blue-dark"
            />
            <span class="ml-3 text-sm text-gray-900">{{ topic.name }}</span>
          </label>

          <!-- No Results -->
          <div
            v-if="filteredTopics.length === 0"
            class="px-4 py-8 text-center text-sm text-gray-500"
          >
            No topics found
          </div>
        </div>

        <!-- Footer Actions -->
        <div class="p-3 border-t border-gray-200 flex justify-between items-center bg-gray-50">
          <button
            @click="clearAll"
            type="button"
            class="text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            Clear all
          </button>
          <button
            @click="isOpen = false"
            type="button"
            class="btn-primary-sm"
          >
            Done
          </button>
        </div>
      </div>
    </div>

    <p class="text-xs text-gray-500 mt-1">
      Select one or more topics that apply to this engagement
    </p>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faTimes, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons'

library.add(faTimes, faChevronDown, faChevronUp)

export default {
  name: 'EngagementTopicSelector',
  components: {
    FontAwesomeIcon
  },
  props: {
    modelValue: {
      type: Array,
      default: () => []
    },
    topics: {
      type: Array,
      required: true
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      isOpen: false,
      searchQuery: ''
    }
  },
  computed: {
    selectedTopics() {
      return this.topics.filter(topic => this.modelValue.includes(topic.id))
    },
    filteredTopics() {
      if (!this.searchQuery) {
        return this.topics
      }
      const query = this.searchQuery.toLowerCase()
      return this.topics.filter(topic =>
        topic.name.toLowerCase().includes(query)
      )
    }
  },
  methods: {
    isSelected(topicId) {
      return this.modelValue.includes(topicId)
    },
    toggleTopic(topicId) {
      const newValue = this.isSelected(topicId)
        ? this.modelValue.filter(id => id !== topicId)
        : [...this.modelValue, topicId]
      this.$emit('update:modelValue', newValue)
    },
    clearAll() {
      this.$emit('update:modelValue', [])
    }
  },
  mounted() {
    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!this.$el.contains(e.target)) {
        this.isOpen = false
      }
    })
  }
}
</script>

<style scoped>
/* Ensure dropdown is above other elements */
.engagement-topic-selector {
  position: relative;
}
</style>
